<script setup lang="ts">
import { ref, onMounted, watch, computed, inject, onUnmounted } from "vue";
import type { Ref } from "vue";
import { APIURL } from "@/defaults";
import { formatPercent, formatNumber } from "@/helpers/formatters";
import type { FundDetails, WsPubSub } from "@/interfaces/interfaces";

const props = defineProps<{
    fund_id: string;
    fund_name: string;
    active: boolean;
    mobile?: boolean;
}>();

const fund: Ref<FundDetails | null> = ref(null);
const dataNotReported = ref(false);
const wsPubSub = inject<WsPubSub>('wsPubSub');

const updateData = () => {
    if (props.fund_id) {
        fetch(`${APIURL}/fund/${props.fund_id}`, { credentials: "include" })
            .then((r) => {
                if (r.status === 404) {
                    dataNotReported.value = true;
                    fund.value = null;
                    return null;
                }
                dataNotReported.value = false;
                return r.json();
            })
            .then((r) => {
                if (r !== null) {
                    fund.value = r;
                }
            });
    }
};

const intradayPerformanceDescriptionPrevious = computed(() => {
    let result = "Performance Yesterday"
    if(fund.value && fund.value.performance_intraday_previous.holdings !== null) {
        result += `\nHoldings ${formatPercent(fund.value.performance_intraday_previous.holdings, 2, true)}`;
    }
    if(fund.value && fund.value.performance_intraday_previous.currencies !== null) {
        result += `\nCurrencies ${formatPercent(fund.value.performance_intraday_previous.currencies, 2, true)}`;
    }
    return result
});

const intradayPerformanceDescription = computed(() => {
    let result = "Performance Today"
    if(fund.value && fund.value.performance_intraday.holdings !== null && fund.value.performance_intraday.currencies !== null) {
        result += `\nHoldings ${formatPercent(fund.value.performance_intraday.holdings, 2, true)}`;
        result += `\nCurrencies ${formatPercent(fund.value.performance_intraday.currencies, 2, true)}`;
    }
    return result
});

let updateInterval: ReturnType<typeof setInterval>;

const handleWsMessage = (data: any) => {
    if (data.event === 'prices_updated') {
        console.log("FundNavItem: Update due to WS event")
        updateData();
    }
};

watch(() => props.fund_id, updateData);

onMounted(() => {
    updateData();
    // Subscribe to websocket messages
    if (wsPubSub) {
        wsPubSub.subscribe(handleWsMessage);
    }
    // Fallback interval every 15 minutes
    updateInterval = setInterval(updateData, 60e3 * 15);
});

onUnmounted(() => {
    if (wsPubSub) {
        wsPubSub.unsubscribe(handleWsMessage);
    }
    clearInterval(updateInterval);
});
</script>

<template>
<a href="#" :class='{"fund-nav-item": true, "--active": props.active, "--mobile": props.mobile}'>
    <div class="fund-nav-item-header">
        <p class="title">{{ props.fund_name }}</p>
        <p class="num-positions" title="Amount of Portfolio Positions" v-if="!props.mobile">{{fund?.num_positions}}</p>
    </div>
    <div class="fund-nav-item-prices">
        <div class="price" :title="fund ? `Closed: ${formatNumber(fund.price_closed, 2)}` : ''">
            <template v-if="dataNotReported"><span class="not-reported">Not reported by Axxion</span></template>
            <template v-else-if="fund && fund.price_intraday">{{formatNumber(fund.price_intraday, 2)}}</template>
            <template v-else>&nbsp;</template>
        </div>
        <div class="fund-nav-item-pricechanges" v-if="fund && !dataNotReported">
            <div
                v-if="fund.performance_intraday_previous != null"
                class="stock-link-price-change"
                :title="intradayPerformanceDescriptionPrevious"
                :class="{'--negative': fund.performance_intraday_previous.total < 0, '--positive': fund.performance_intraday_previous.total > 0}"
            >
                {{formatPercent(fund.performance_intraday_previous.total, 2, true)}}
            </div>
            <div
                class="stock-link-price-change"
                :title="intradayPerformanceDescription"
                v-if="fund.performance_intraday != null"
                :class="{'--negative': fund.performance_intraday.total < 0, '--positive': fund.performance_intraday.total > 0}"
            >
                {{formatPercent(fund.performance_intraday.total, 2, true)}}
            </div>
        </div>
    </div>
</a>
</template>

<style>
.fund-nav-item {
  display: flex;
  background: #fff;
  padding: 10px 10px;
  flex: 0 1 25%;
  color: #000;
  flex-flow: column;
  justify-content: space-between;
  box-shadow: 0 0 10px rgba(0, 20, 40, 0.2);
  border-radius: 0 0 5px 5px;
  text-decoration: none;
}

.fund-nav-item:hover {
    background: rgb(247, 229, 218);
    color: inherit;
}

.fund-nav-item.--active {
    background: rgb(247, 229, 218);
    box-shadow: 0 4px 0 rgb(226, 159, 118) inset, 0 0 10px rgba(0, 20, 40, 0.2);
}

/* Mobile-specific styles */
.fund-nav-item.--mobile {
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.fund-nav-item.--mobile.--active {
    box-shadow: 0 0 0 2px rgb(226, 159, 118), 0 1px 3px rgba(0, 0, 0, 0.1);
}

.fund-nav-item-header {
    display: flex;
    flex-flow: row;
    justify-content: space-between;
}

.fund-nav-item .title,
.fund-nav-item .num-positions {
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.fund-nav-item.--mobile .title {
    text-transform: none;
    font-size: 14px;
    letter-spacing: normal;
}

.fund-nav-item .num-positions {
    font-weight: normal;
    background: rgba(0,0,0,0.2);
    padding: 0 4px;
    border-radius: 3px;
    color: #fff;
    align-self: flex-start;
}

.fund-nav-item.--mobile .num-positions {
    font-size: 12px;
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 6px;
    border-radius: 10px;
}

.fund-nav-item .price {
    font-weight: 600;
    line-height: 1;
}

.fund-nav-item.--mobile .price {
    font-size: 16px;
}

.fund-nav-item-prices {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
}

.fund-nav-item.--mobile .fund-nav-item-prices {
    text-align: right;
}

.fund-nav-item-pricechanges {
    display: flex;
    gap: 4px;
    align-items: flex-end;
}

.fund-nav-item-pricechanges > .stock-link-price-change:first-child {
    font-size: 0.6em;
    opacity: 0.5;
}

.not-reported {
    font-size: 12px;
    font-weight: 600;
    color: #999;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}
</style>
